import { hasValue, isRecord } from "@arti/utilities";
import { type FC, useEffect, useState } from "react";

import type { ResumeUnavailableRefusal } from "../useChannelEnvironment";

import {
  prepareCheckpointRecoveryFromAReplacedHostAsUser,
  previewCheckpointRecoveryFromAReplacedHostAsUser,
  type DashboardLostHostRecoveryPreviewResponse,
} from "../../../dashboard-client";
import { useDashboardAuth } from "../../../hooks/useDashboardAuth";
import { Button } from "../../common/Button";
import { Modal } from "../../common/Modal";

type LostHostRecoveryPreview = DashboardLostHostRecoveryPreviewResponse;

export function recoveryFailureMessage(
  error: unknown,
  fallback: string,
): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (!isRecord(error)) {
    return fallback;
  }
  const message =
    typeof error.message === "string" && error.message.length > 0
      ? error.message
      : fallback;
  return [
    message,
    typeof error.reason === "string" ? `Reason: ${error.reason}.` : null,
    typeof error.cas_scope === "string"
      ? `Changed state: ${error.cas_scope}.`
      : null,
    typeof error.operation_id === "string"
      ? `Operation: ${error.operation_id}.`
      : null,
  ]
    .filter(hasValue)
    .join(" ");
}

/** Title + one-line explanation per refusal reason. Distinguishes a
 *  cluster-wide admission failure (initial attach: no node has room) from a
 *  pinned-node resume failure and from storage/manifest problems, so the modal
 *  never mislabels one as the other. */
function refusalCopy(reason: ResumeUnavailableRefusal["reason"]): {
  title: string;
  blurb: string;
} {
  switch (reason) {
    case "node_maintenance": {
      return {
        blurb:
          "This environment's node is under maintenance. Its retained state is unchanged; retry after maintenance completes.",
        title: "Node Maintenance",
      };
    }
    case "cluster_capacity_unavailable": {
      return {
        blurb:
          "No node in the cluster currently has room for this environment. Its state is safe; nothing was lost.",
        title: "Capacity Unavailable",
      };
    }
    case "pinned_node_compute_unavailable": {
      return {
        blurb:
          "The node this environment is pinned to can't fit it right now. Its state is safe; nothing was lost.",
        title: "Resume Refused — Pinned Node Full",
      };
    }
    case "pinned_node_offline": {
      return {
        blurb:
          "The node this environment is pinned to is unreachable right now. Its state is retained.",
        title: "Pinned Node Offline",
      };
    }
    case "pinned_node_capability_mismatch": {
      return {
        blurb:
          "The pinned node no longer meets this environment's requirements. Its state is retained.",
        title: "Pinned Node Incompatible",
      };
    }
    case "pinned_host_changed": {
      return {
        blurb:
          "This environment's pinned host changed, so its retained local state is unavailable. A durable checkpoint recovery may still be possible.",
        title: "Pinned Host Changed",
      };
    }
    case "storage_unreachable": {
      return {
        blurb:
          "The environment's held storage can't be reached right now. Nothing was deleted.",
        title: "Storage Unreachable",
      };
    }
    case "manifest_corrupt":
    case "manifest_incompatible": {
      return {
        blurb:
          "The saved environment manifest can't be restored as-is. Its held storage is listed below.",
        title: "Environment Manifest Problem",
      };
    }
    default: {
      return {
        blurb: "This environment can't resume right now. Its state is safe.",
        title: "Resume Refused",
      };
    }
  }
}

export function shouldShowResumeRefusalCompute(
  reason: ResumeUnavailableRefusal["reason"],
): boolean {
  return (
    reason === "cluster_capacity_unavailable" ||
    reason === "pinned_node_compute_unavailable"
  );
}

export function canPreviewLostHostRecovery(
  reason: ResumeUnavailableRefusal["reason"],
  globalRole: string | null,
): boolean {
  return (
    globalRole === "admin" &&
    (reason === "pinned_host_changed" || reason === "storage_unreachable")
  );
}

interface ResumeRefusalCapacityRow {
  cells: [string, number, number, number];
  tone?: string;
}

const ResumeRefusalComputeTable: FC<{ rows: ResumeRefusalCapacityRow[] }> = ({
  rows,
}) => (
  <div>
    <p className="mb-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
      compute
    </p>
    <table className="w-full table-fixed border-collapse font-mono text-[11px] tabular-nums">
      <thead>
        <tr className="text-slate-400 dark:text-slate-500">
          <th className="w-1/3 pb-1 text-left text-[9px] font-bold uppercase tracking-[0.16em]" />
          <th className="pb-1 text-right text-[9px] font-bold uppercase tracking-[0.16em]">
            cpu (m)
          </th>
          <th className="pb-1 text-right text-[9px] font-bold uppercase tracking-[0.16em]">
            ram (MiB)
          </th>
          <th className="pb-1 text-right text-[9px] font-bold uppercase tracking-[0.16em]">
            live slots
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.cells[0]}
            className={`border-t border-slate-100 dark:border-slate-800/60 ${row.tone ?? "text-slate-600 dark:text-slate-300"}`}
          >
            <td className="py-1 text-left uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
              {row.cells[0]}
            </td>
            <td className="py-1 text-right">{row.cells[1]}</td>
            <td className="py-1 text-right">{row.cells[2]}</td>
            <td className="py-1 text-right">{row.cells[3]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Capacity-refusal modal: shows exactly what was requested vs available, the
 *  deficit, and the storage still held on disk — so the operator can act
 *  (retry later, or trim resources) instead of reading a wall-of-text error. */
export const ResumeRefusalModal: FC<{
  refusal: ResumeUnavailableRefusal;
  channelId: string;
  retrying: boolean;
  /** Only true when an environment exists to edit — the admission (initial
   *  attach) path may have no environment yet, so "Edit resources" would be
   *  inert. */
  canEditResources: boolean;
  onRetry: () => void;
  onEditResources: () => void;
  onClose: () => void;
  onRecoveryPrepared: () => Promise<void>;
}> = ({
  refusal,
  channelId,
  retrying,
  canEditResources,
  onRetry,
  onEditResources,
  onClose,
  onRecoveryPrepared,
}) => {
  const { globalRole } = useDashboardAuth();
  const [recoveryPreview, setRecoveryPreview] =
    useState<LostHostRecoveryPreview | null>(null);
  const [recoveryPreviewError, setRecoveryPreviewError] = useState<
    string | null
  >(null);
  const [recoveryPreviewLoading, setRecoveryPreviewLoading] = useState(false);
  const [confirmingRecovery, setConfirmingRecovery] = useState(false);
  const [acknowledgedLoss, setAcknowledgedLoss] = useState(false);
  const [preparingRecovery, setPreparingRecovery] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const { available, deficit, requested, storageHeld, suggestedActions } =
    refusal;
  const copy = refusalCopy(refusal.reason);
  const showCompute = shouldShowResumeRefusalCompute(refusal.reason);
  const showRetry = refusal.retryable;
  const showEdit =
    canEditResources && suggestedActions.includes("downscale_cpu_ram");
  const isLostHostReason =
    refusal.reason === "pinned_host_changed" ||
    refusal.reason === "storage_unreachable";
  const canInspectRecovery = canPreviewLostHostRecovery(
    refusal.reason,
    globalRole,
  );

  useEffect(() => {
    if (!canInspectRecovery) {
      return;
    }
    const controller = new AbortController();
    setRecoveryPreviewLoading(true);
    setRecoveryPreviewError(null);
    void previewCheckpointRecoveryFromAReplacedHostAsUser({
      path: { channelId },
      signal: controller.signal,
      throwOnError: true,
    })
      .then(({ data }) => setRecoveryPreview(data))
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setRecoveryPreviewError(
            recoveryFailureMessage(error, "Recovery preview failed."),
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setRecoveryPreviewLoading(false);
        }
      });
    return () => controller.abort();
  }, [canInspectRecovery, channelId]);

  const prepareRecovery = async () => {
    if (!recoveryPreview?.eligible) {
      return;
    }
    setPreparingRecovery(true);
    setRecoveryError(null);
    try {
      await prepareCheckpointRecoveryFromAReplacedHostAsUser({
        body: {
          acknowledgedPermanentLoss: true,
          idempotencyKey: recoveryPreview.idempotencyKey,
          operationId: recoveryPreview.operationId,
          snapshot: recoveryPreview.snapshot,
        },
        path: { channelId },
        throwOnError: true,
      });
      await onRecoveryPrepared();
    } catch (error) {
      setRecoveryError(
        recoveryFailureMessage(error, "Recovery preparation failed."),
      );
    } finally {
      setPreparingRecovery(false);
    }
  };

  // Held aggregates overlap: workspaceMb includes runtime + archive, rootfsMb
  // includes the committed image. Derive DISJOINT segments so the bar and total
  // aren't double-counted; total is the two authoritative aggregates.
  const total = storageHeld.workspaceMb + storageHeld.rootfsMb;
  const workspaceBaseMb = Math.max(
    0,
    storageHeld.workspaceMb -
      storageHeld.runtimeMb -
      storageHeld.hibernateArchiveMb,
  );
  const rootfsBaseMb = Math.max(
    0,
    storageHeld.rootfsMb - storageHeld.committedImageMb,
  );
  const segments = [
    {
      class: "bg-emerald-500 dark:bg-emerald-400",
      label: "workspace",
      mb: workspaceBaseMb,
    },
    {
      class: "bg-slate-400 dark:bg-slate-500",
      label: "runtime",
      mb: storageHeld.runtimeMb,
    },
    {
      class: "bg-violet-500 dark:bg-violet-400",
      label: "archive",
      mb: storageHeld.hibernateArchiveMb,
    },
    { class: "bg-sky-500 dark:bg-sky-400", label: "rootfs", mb: rootfsBaseMb },
    {
      class: "bg-amber-500 dark:bg-amber-400",
      label: "image",
      mb: storageHeld.committedImageMb,
    },
  ];
  const shown = segments.filter((segment) => segment.mb > 0);

  const capacityRows: ResumeRefusalCapacityRow[] = showCompute
    ? [
        {
          cells: [
            "requested",
            requested.cpuMilli,
            requested.ramMb,
            requested.liveEnvironments,
          ],
        },
        {
          cells: [
            "available",
            available.cpuMilli,
            available.ramMb,
            available.liveEnvironments,
          ],
        },
        {
          cells: [
            "deficit",
            deficit.cpuMilli,
            deficit.ramMb,
            deficit.liveEnvironments,
          ],
          tone: "text-rose-600 dark:text-rose-400",
        },
      ]
    : [];

  return (
    <Modal
      title={copy.title}
      subtitle={copy.blurb}
      widthClass="max-w-xl"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
          {showEdit && (
            <Button variant="outline" size="sm" onClick={onEditResources}>
              Edit resources
            </Button>
          )}
          {showRetry && (
            <Button
              variant="primary"
              size="sm"
              loading={retrying}
              loadingText="Retrying…"
              onClick={onRetry}
            >
              Retry
            </Button>
          )}
          {recoveryPreview?.eligible && !confirmingRecovery && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setConfirmingRecovery(true)}
            >
              Recover from lost host…
            </Button>
          )}
          {recoveryPreview?.eligible && confirmingRecovery && (
            <Button
              variant="primary"
              size="sm"
              disabled={!acknowledgedLoss}
              loading={preparingRecovery}
              loadingText="Preparing…"
              onClick={() => void prepareRecovery()}
            >
              Prepare recovery
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {showCompute && <ResumeRefusalComputeTable rows={capacityRows} />}

        <div>
          <p className="mb-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
            storage held · {total} MiB
          </p>
          {total > 0 ? (
            <>
              <div className="flex h-2 w-full overflow-hidden">
                {shown.map((segment) => (
                  <div
                    key={segment.label}
                    className={segment.class}
                    style={{ width: `${(segment.mb / total) * 100}%` }}
                    title={`${segment.label} ${segment.mb} MiB`}
                  />
                ))}
              </div>
              <StorageLegend shown={shown} />
            </>
          ) : (
            <p className="font-mono text-[11px] text-slate-400 dark:text-slate-600">
              no storage held
            </p>
          )}
        </div>

        {isLostHostReason && globalRole !== "admin" && (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Contact a platform administrator to assess checkpoint recovery.
          </p>
        )}
        {canInspectRecovery && recoveryPreviewLoading && (
          <p className="text-sm text-slate-500">Checking recovery options…</p>
        )}
        {canInspectRecovery && recoveryPreviewError && (
          <p className="text-sm text-rose-600 dark:text-rose-400">
            {recoveryPreviewError}
          </p>
        )}
        {canInspectRecovery && recoveryPreview && !recoveryPreview.eligible && (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Recovery unavailable: {recoveryPreview.reason.replaceAll("_", " ")}.
          </p>
        )}
        {recoveryPreview?.eligible && confirmingRecovery && (
          <div className="space-y-3 border-t border-slate-200 pt-4 dark:border-slate-700">
            <div className="font-mono text-[11px] text-slate-600 dark:text-slate-300">
              <p>
                checkpoint{" "}
                {new Date(
                  recoveryPreview.checkpoint.createdAt,
                ).toLocaleString()}
                , generation {recoveryPreview.checkpoint.generation}, sequence{" "}
                {recoveryPreview.checkpoint.sequence}
              </p>
              <p>
                source {recoveryPreview.source.nodeId} /{" "}
                {recoveryPreview.source.hostIdentity}
              </p>
              <p>
                replacement {recoveryPreview.replacement.nodeId} /{" "}
                {recoveryPreview.replacement.hostIdentity}
              </p>
              <p>releases {recoveryPreview.releasedStorageMb} MiB</p>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Workspace checkpoints can restore files, worktree patches,
              untracked files and unpushed commits. Container writable-layer
              changes, temporary paths, excluded secrets/caches and changes
              after the checkpoint cannot be recovered.
            </p>
            <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                checked={acknowledgedLoss}
                className="mt-0.5"
                type="checkbox"
                onChange={(event) => setAcknowledgedLoss(event.target.checked)}
              />
              I confirm the original host is permanently lost and accept the
              listed unrecoverable data.
            </label>
            {recoveryError && (
              <p className="text-sm text-rose-600 dark:text-rose-400">
                {recoveryError}
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

interface StorageLegendProps {
  shown: { class: string; label: string; mb: number }[];
}

function StorageLegend({ shown }: StorageLegendProps) {
  return (
    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[9px] tabular-nums text-slate-500 dark:text-slate-400">
      {shown.map((segment) => (
        <span key={segment.label} className="inline-flex items-center gap-1">
          <span
            aria-hidden
            className={`inline-block h-1.5 w-1.5 ${segment.class}`}
          />
          {segment.label} {segment.mb}
        </span>
      ))}
    </div>
  );
}
